import { CommonModule, DatePipe, registerLocaleData } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import localePt from '@angular/common/locales/pt';
import {
  APP_INITIALIZER,
  importProvidersFrom,
  LOCALE_ID,
  NgModule
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDatepickerModule } from '@angular/material/datepicker';

import { MatIconRegistry } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import {
  MAT_SNACK_BAR_DEFAULT_OPTIONS,
  MatSnackBarModule
} from '@angular/material/snack-bar';

import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MAT_TABS_CONFIG } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BrowserModule, DomSanitizer } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateModule } from '@ngx-translate/core';
import {
  CalendarModule as CalendarioAngular,
  DateAdapter
} from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/moment';
import * as moment from 'moment';
import { NgxMaskModule } from 'ngx-mask';
import { BadgeModule } from 'primeng/badge';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { CardModule } from 'primeng/card';
import { ProgressBarModule } from 'primeng/progressbar';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { StyleClassModule } from 'primeng/styleclass';
import { TimelineModule } from 'primeng/timeline';
import { TooltipModule } from 'primeng/tooltip';
import SwiperCore, {
  Autoplay,
  EffectCoverflow,
  Navigation,
  Pagination
} from 'swiper';
import { SwiperModule } from 'swiper/angular';
import {
  DomainService,
  TokenService,
  UserService,
  UserSettingsService,
  ViggoCoreBreadCrumbModule,
  ViggoCoreChartModule,
  ViggoCoreDashboardDefaultModule,
  ViggoCoreFooterModule,
  ViggoCoreHeaderModule,
  ViggoCoreModule,
  ViggoCorePageAccessDeniedModule,
  ViggoCorePageErrorModule,
  ViggoCorePageNotFoundModule,
  ViggoCoreSideMenuModule,
  ViggoCoreToolBarModule,
  ViggoCoreWorkSpaceModule
} from 'viggo-sistemas-core-web';
import { environment } from '../environments/environment';
import {
  DashboardComponent,
  LoggedComponent,
  RecoveryScreenComponent
} from './app-components';
import { DashboardInfoComponent } from './app-components/dashboard/components/dashboard-info/dashboard-info.component';
import { DashboardInfoService } from './app-components/dashboard/service/dashboard.service';
import { RecoveryManagerService } from './app-components/recovery-screen/services/recovery-manager.service';
import { AppComponent } from './app.component';
import { CustomMenuService } from './global services';
import { BuildDetailsHttpService } from './global services/build-services/build-details.http.service';
import { FastMenuService } from './global services/fast-menu-service/fast-menu-service';
import { FastCadastroModule } from './modules/fast-cadastro/fast-cadastro.module';
import { AppRoutingModule } from './routes/routes';
import { CancelContaDialogComponent } from './shared/components/cancel-conta-dialog/cancel-conta-dialog.component';
import { UnloggedGuard } from './shared/guards/unloged.guard';
import { AuthService } from './shared/services/auth.service';
import { CacheService } from './shared/services/cache.service';
import { HealthMonitorService } from './shared/services/health_monitor.service';
import { SharedModule } from './shared/shared.module';

export function fetchBuildDetails(
  buildDetailsService: BuildDetailsHttpService
) {
  return () => buildDetailsService.fetchBuildDetails();
}

export function clearIndexHtmlCache(cacheService: CacheService) {
  return () => cacheService.clearCacheForFile('index.html');
}

export function initializeApp(
  cacheService: CacheService,
  healthMonitor: HealthMonitorService,
  recoveryManager: RecoveryManagerService,
  tokenService: TokenService
) {
  return async () => {
    try {
      // Verificar se estamos vindo de uma recuperação recente
      const urlParams = new URLSearchParams(window.location.search);
      const noRecovery = urlParams.get('noRecovery') === 'true';

      // Verificar se estamos em modo seguro
      const inSafeMode = localStorage.getItem('safe-mode') === 'true';

      if (inSafeMode) {
        console.log('Aplicativo iniciando em modo seguro');
        // Carrega apenas o mínimo necessário para funcionamento
        return true;
      }

      // Verificar se acabamos de fazer uma recuperação
      if (noRecovery) {
        console.log('Ignorando verificações iniciais após recuperação');

        // Limpar o parâmetro da URL após processar
        if (window.history && window.history.replaceState) {
          const url = window.location.pathname;
          window.history.replaceState({}, document.title, url);
        }

        return true;
      }

      // Definir chaves críticas com seus requisitos
      const criticalKeysConfig = [
        {
          key: 'token',
          requirements: {
            type: 'json'
          }
        },
        {
          key: 'user',
          requirements: {
            type: 'json'
          }
        },
        {
          key: 'domain',
          requirements: {
            type: 'json'
          }
        },
        {
          key: 'menu_items',
          requirements: { type: 'array_json' }
        }
        // Adicione outras chaves importantes da sua aplicação
      ];

      // Adicionar as chaves críticas ao serviço de cache com seus requisitos
      criticalKeysConfig.forEach(config => {
        cacheService.addCriticalLocalStorageKey(
          config.key,
          config.requirements
        );
      });

      // Verificar saúde do cache e localStorage
      const isHealthy = await cacheService.checkCacheHealth();

      if (!isHealthy) {
        console.warn('Cache ou localStorage não estão saudáveis');

        // Verificar se há problemas específicos no localStorage
        const problematicKeys = cacheService.checkLocalStorageIntegrity();

        // Verificar se devemos tentar recuperação
        let reason = 'Problemas detectados na inicialização';
        if (problematicKeys.length > 0) {
          reason = `Chaves críticas comprometidas: ${problematicKeys.join(
            ', '
          )}`;
        }

        if (recoveryManager.shouldInitiateRecovery(reason)) {
          await healthMonitor.attemptRecovery(reason);
        }
      }

      // Limpar cache específico
      await cacheService.clearCacheForFile('index.html');

      // Iniciar monitoramento contínuo
      healthMonitor.startMonitoring();

      return true;
    } catch (error) {
      console.error('Erro durante inicialização:', error);
      // Continuar mesmo com erro
      return true;
    }
  };
}

registerLocaleData(localePt);
// install Swiper modules
SwiperCore.use([EffectCoverflow, Pagination, Autoplay, Navigation]);

function momentAdapterFactory() {
  return adapterFactory(moment);
}
@NgModule({
  declarations: [
    AppComponent,
    LoggedComponent,
    DashboardComponent,
    DashboardInfoComponent,
    CancelContaDialogComponent,
    RecoveryScreenComponent
  ],
  imports: [
    CommonModule,
    FormsModule,
    BrowserModule,
    HttpClientModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    // requerido import no raiz
    NgxMaskModule.forRoot({ validation: false }),
    // rotas
    AppRoutingModule,
    CalendarioAngular.forRoot({
      provide: DateAdapter,
      useFactory: momentAdapterFactory
    }),
    SharedModule,
    FastCadastroModule,
    ViggoCoreModule.forRoot({
      interceptors: {
        includeAuthInterceptor: { enable: true }
      }
    }),
    SwiperModule,
    // material modules
    MatSnackBarModule,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
    MatDialogModule,
    MatCardModule,
    MatInputModule,
    MatDatepickerModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    // primeng modules
    CardModule,
    CalendarModule,
    TimelineModule,
    StyleClassModule,
    ButtonModule,
    TooltipModule,
    BadgeModule,
    ProgressSpinnerModule,
    ProgressBarModule,
    // ViggoCore modulos
    ViggoCoreWorkSpaceModule,
    ViggoCoreDashboardDefaultModule,
    ViggoCoreFooterModule,
    ViggoCoreBreadCrumbModule,
    ViggoCorePageNotFoundModule,
    ViggoCoreSideMenuModule,
    ViggoCoreToolBarModule,
    ViggoCoreHeaderModule,
    ViggoCoreChartModule,
    ViggoCorePageErrorModule,
    ViggoCorePageNotFoundModule,
    ViggoCorePageAccessDeniedModule,
    //
    StoreModule.forRoot({}, {}),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: environment.production,
      connectInZone: true
    }),
    EffectsModule.forRoot([]),
    TranslateModule.forRoot()
  ],
  providers: [
    UserService,
    DomainService,
    CustomMenuService,
    FastMenuService,
    UnloggedGuard,
    DashboardInfoService,
    AuthService,
    CacheService,
    UserSettingsService,
    HealthMonitorService,
    RecoveryManagerService,
    DatePipe,
    {
      provide: APP_INITIALIZER,
      useFactory: fetchBuildDetails,
      deps: [BuildDetailsHttpService],
      multi: true
    },
    {
      provide: APP_INITIALIZER,
      useFactory: clearIndexHtmlCache,
      deps: [CacheService],
      multi: true
    },
    // {
    //   provide: APP_INITIALIZER,
    //   useFactory: initializeApp,
    //   deps: [
    //     CacheService,
    //     HealthMonitorService,
    //     RecoveryManagerService,
    //     TokenService
    //   ],
    //   multi: true
    // },
    // WINDOW_PROVIDERS,
    { provide: LOCALE_ID, useValue: 'pt-BR' },
    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: { duration: 4000 }
    },
    {
      provide: MAT_TABS_CONFIG,
      useValue: {
        animationDuration: '300ms'
      }
    },
    importProvidersFrom(HttpClientModule),
    // contasInterceptorProvider // TODO: ACESSE ESSE LINK E CRIE UM ARQUIVO COM TODOS OS INTERCEPTS DE TODOS OS MODULOS PAPAI https://v17.angular.io/guide/http-intercept-requests-and-responses
    // {
    //   provide: HTTP_INTERCEPTORS,
    //   useClass: GlobalHttpInterceptorService,
    //   multi: true
    // },
    provideAnimationsAsync('noop')
    // {provide: APP_BASE_HREF, useValue: '/src/app'}
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor(matIconRegistry: MatIconRegistry, domSanitizer: DomSanitizer) {
    // fazendo registro dos paths de icones
    matIconRegistry.addSvgIconSet(
      domSanitizer.bypassSecurityTrustResourceUrl('./assets/icons.svg')
    );
  }
}
